import type { CardStatsPeriod } from "../types/card-stats.types";

type SpendBucket = CardStatsPeriod["bucket"];

/** Most axis labels the chart prints; past this it labels every Nth bucket, anchored on the latest one. */
const MAX_SPEND_TICKS = 7;

export const cardStatsLocale = (lang: string): string =>
  lang === "es" ? "es-ES" : lang === "pt" ? "pt-BR" : "en-US";

/**
 * The bucket start as a calendar day. The backend sends `YYYY-MM-DD` (`bucket::date::text`); `new Date()`
 * would read that as UTC midnight, which is the previous day west of Greenwich, so every formatter below pins
 * `timeZone: "UTC"` to the same instant.
 */
const parseBucketDate = (date: string): Date | null => {
  const [year, month, day] = date.split("-").map(Number);
  if (!year || !month || !day) return null;
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const partsOf = (date: Date, locale: string, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(locale, { ...options, timeZone: "UTC" }).formatToParts(date);

const capitalize = (text: string) => text.charAt(0).toLocaleUpperCase() + text.slice(1);

/**
 * The short axis label of a bucket: "mar 20" for a day, "20 oct" / "Oct 20" for a week (its Monday), "oct"
 * for a month. Built from the parts so the connectors a locale puts in a short date ("20 de out.") stay out
 * of a label that has 40px to fit in.
 */
export function formatSpendBucketTick(date: string, bucket: SpendBucket, locale: string): string {
  const parsed = parseBucketDate(date);
  if (!parsed) return date;

  if (bucket === "day") {
    const parts = partsOf(parsed, locale, { weekday: "short", day: "numeric" });
    const weekday = parts.find((p) => p.type === "weekday")?.value ?? "";
    const day = parts.find((p) => p.type === "day")?.value ?? "";
    return `${weekday} ${day}`.trim();
  }
  if (bucket === "week") {
    return partsOf(parsed, locale, { day: "numeric", month: "short" })
      .filter((p) => p.type === "day" || p.type === "month")
      .map((p) => p.value)
      .join(" ");
  }
  return partsOf(parsed, locale, { month: "short" })
    .filter((p) => p.type === "month")
    .map((p) => p.value)
    .join("");
}

/**
 * The bucket written out for the chart's readout: "Martes, 20 de octubre", "20 de octubre" (the caller wraps
 * it in "Semana del …") or "Octubre de 2026".
 */
export function formatSpendBucketDate(date: string, bucket: SpendBucket, locale: string): string {
  const parsed = parseBucketDate(date);
  if (!parsed) return date;

  const options: Intl.DateTimeFormatOptions =
    bucket === "day"
      ? { weekday: "long", day: "numeric", month: "long" }
      : bucket === "week"
        ? { day: "numeric", month: "long" }
        : { month: "long", year: "numeric" };
  const text = new Intl.DateTimeFormat(locale, { ...options, timeZone: "UTC" }).format(parsed);
  return bucket === "week" ? text : capitalize(text);
}

/**
 * Whether the axis labels the bucket at `index`. Ninety days are fourteen weekly bars, more labels than the
 * column next to the card sidebar can print, so past `MAX_SPEND_TICKS` only every Nth is kept — counted back
 * from the latest bucket, which is always labelled because it is the one the readout starts on.
 */
export function isSpendTickShown(index: number, count: number): boolean {
  const every = Math.max(1, Math.ceil(count / MAX_SPEND_TICKS));
  return (count - 1 - index) % every === 0;
}
